import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, FileText, Newspaper, User, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";

const KIND_ICON = { page: FileText, blog: Newspaper, creator: User, faq: HelpCircle };
const KIND_LABEL = { page: "Page", blog: "Blog", creator: "Creator", faq: "FAQ" };

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQ(""); setResults([]);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setResults([]); return; }
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q } });
        setResults(r.data.results || []);
      } catch { setResults([]); }
      finally { setBusy(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (path) => { onClose(); navigate(path); };

  return (
    <div className="fixed inset-0 z-[95] flex items-start justify-center pt-[10vh] px-4"
      role="dialog" aria-modal="true" aria-label="Site search" data-testid="search-overlay">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-2xl card-dt !bg-dt-bg2/95 backdrop-blur-xl overflow-hidden shadow-2xl">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-dt-border">
          <Search className="w-5 h-5 text-dt-accent" />
          <input
            ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search pages, creators, blog, FAQ…"
            className="flex-1 bg-transparent text-dt-text text-lg placeholder:text-dt-muted focus:outline-none"
            data-testid="search-input"
          />
          <button onClick={onClose} aria-label="Close search" className="text-dt-muted hover:text-dt-text" data-testid="search-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {q.trim().length >= 2 && !busy && results.length === 0 && (
            <p className="px-5 py-8 text-sm text-dt-muted text-center" data-testid="search-empty">No results for “{q}”.</p>
          )}
          {results.map((r, i) => {
            const Icon = KIND_ICON[r.kind] || FileText;
            return (
              <button key={i} onClick={() => go(r.path)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-dt-card transition-colors text-left border-b border-dt-border/40 last:border-0"
                data-testid={`search-result-${i}`}>
                <Icon className="w-4 h-4 text-dt-accent shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-dt-text truncate">{r.title}</p>
                  {r.snippet && <p className="text-xs text-dt-muted truncate">{r.snippet}</p>}
                </div>
                <span className="text-[10px] uppercase tracking-widest text-dt-muted shrink-0">{KIND_LABEL[r.kind] || r.kind}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
